import React from 'react';
import { ArrowRight, Users, Briefcase, Zap, TrendingUp, Clock, CheckCircle, Plus } from 'lucide-react';

export default function VendorDashboard({ user, bench, requirements, setActive }) {
  const immediate = (bench || []).filter(b => b.availability === 'immediate').length;
  const liveJobs = (requirements || []).length;

  const fakeJobs = requirements?.length > 0 ? requirements : [
    { _id:'f1', title:'Senior React Developer', experience:3, adminBudget:80000, skills:['React','Node.js'] },
    { _id:'f2', title:'Full Stack Engineer', experience:4, adminBudget:90000, skills:['Vue','Python'] },
    { _id:'f3', title:'DevOps Engineer', experience:5, adminBudget:100000, skills:['Docker','AWS'] },
    { _id:'f4', title:'Python Backend Developer', experience:3, adminBudget:75000, skills:['Python','Django'] },
    { _id:'f5', title:'Mobile App Developer', experience:2, adminBudget:65000, skills:['React Native','iOS'] },
    { _id:'f6', title:'Data Engineer', experience:4, adminBudget:95000, skills:['Spark','Kafka'] },
  ];

  const stats = [
    { label: 'Bench Resources', value: bench?.length || 0, icon: Users, change: 'Total candidates', color: '#6366f1' },
    { label: 'Immediately Available', value: immediate, icon: Zap, change: 'Ready to deploy', color: '#16a34a' },
    { label: 'Live Jobs', value: liveJobs, icon: Briefcase, change: 'Open positions', color: '#f59e0b' },
    { label: 'Placements', value: 0, icon: CheckCircle, change: 'This month', color: '#8b5cf6' },
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", sans-serif' }}>
      <style>{`
        @keyframes scrollUp { 0%{transform:translateY(0)} 100%{transform:translateY(-50%)} }
        .jobs-scroll { overflow:hidden; height:220px; }
        .jobs-inner { animation:scrollUp 12s linear infinite; }
        .jobs-inner:hover { animation-play-state:paused; }
      `}</style>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#0f172a', fontSize: '20px', fontWeight: '700', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : 'Afternoon'}, {user.name.split(' ')[0]}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
          Here's an overview of your bench and active opportunities.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb', transition: 'all 0.15s', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color={s.color} />
                </div>
              </div>
              <div style={{ color: '#0f172a', fontSize: '24px', fontWeight: '700', letterSpacing: '-0.5px', marginBottom: '3px' }}>{s.value}</div>
              <div style={{ color: '#0f172a', fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>{s.label}</div>
              <div style={{ color: '#9ca3af', fontSize: '11px' }}>{s.change}</div>
            </div>
          );
        })}
      </div>

      {/* Two column */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

        {/* Quick Actions */}
        <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: 0 }}>Quick Actions</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {[
              { label: 'Add Bench Resource', desc: 'Add a new candidate to your bench', action: 'bench', icon: Plus, color: '#6366f1' },
              { label: 'Browse Live Jobs', desc: 'Find matching opportunities', action: 'live', icon: Briefcase, color: '#16a34a' },
              { label: 'View AI Matches', desc: 'See recommended job matches', action: 'recommendations', icon: Zap, color: '#f59e0b' },
              { label: 'Interview Slots', desc: 'Manage scheduled interviews', action: 'schedule', icon: Clock, color: '#8b5cf6' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <div key={a.label} onClick={() => setActive(a.action)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 8px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '7px', background: `${a.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={15} color={a.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#111827', fontSize: '13px', fontWeight: '500' }}>{a.label}</div>
                    <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '1px' }}>{a.desc}</div>
                  </div>
                  <ArrowRight size={13} color="#d1d5db" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Live Opportunities - Auto Scroll */}
        <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }}/>
              <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: 0 }}>Live Opportunities</h3>
            </div>
            <button onClick={() => setActive('live')} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="jobs-scroll">
            <div className="jobs-inner">
              {[...fakeJobs, ...fakeJobs].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #f9fafb', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setActive('live')}>
                  <div>
                    <div style={{ color: '#111827', fontSize: '13px', fontWeight: '500' }}>{r.title}</div>
                    <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>{r.experience} yrs · ₹{(r.adminBudget || r.budget)?.toLocaleString()}/mo</div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {(r.skills || []).slice(0, 2).map(s => (
                      <span key={s} style={{ padding: '2px 7px', background: '#f3f4f6', color: '#374151', borderRadius: '4px', fontSize: '10px' }}>{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bench Table */}
      <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: '0 0 2px' }}>My Bench Resources</h3>
            <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>{bench?.length || 0} candidates available</p>
          </div>
          <button onClick={() => setActive('bench')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' }}>
            <Plus size={13} /> Add Resource
          </button>
        </div>
        {(bench || []).length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Name', 'Title', 'Experience', 'Rate', 'Availability', 'Skills'].map(h => (
                  <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(bench || []).slice(0, 6).map((b, i) => (
                <tr key={b._id} style={{ borderTop: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: ['#ede9fe','#dcfce7','#dbeafe','#fef9c3'][i%4], display: 'flex', alignItems: 'center', justifyContent: 'center', color: ['#6366f1','#16a34a','#2563eb','#ca8a04'][i%4], fontWeight: '700', fontSize: '12px', flexShrink: 0 }}>
                        {b.isAnonymous ? '?' : b.name?.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ color: '#111827', fontWeight: '500', fontSize: '13px' }}>{b.isAnonymous ? 'Anonymous' : b.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px', color: '#374151', fontSize: '13px' }}>{b.title}</td>
                  <td style={{ padding: '12px 20px', color: '#6b7280', fontSize: '13px' }}>{b.experience} yrs</td>
                  <td style={{ padding: '12px 20px', color: '#6b7280', fontSize: '13px' }}>₹{b.rate?.toLocaleString()}/{b.rateType}</td>
                  <td style={{ padding: '12px 20px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', background: b.availability === 'immediate' ? '#f0fdf4' : '#f9fafb', color: b.availability === 'immediate' ? '#16a34a' : '#6b7280' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: b.availability === 'immediate' ? '#16a34a' : '#9ca3af' }}/>
                      {b.availability === 'immediate' ? 'Immediate' : b.availability}
                    </span>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {(b.skills || []).slice(0, 2).map(s => (
                        <span key={s} style={{ padding: '2px 7px', background: '#f3f4f6', color: '#374151', borderRadius: '4px', fontSize: '10px', fontWeight: '500' }}>{s}</span>
                      ))}
                      {b.skills?.length > 2 && <span style={{ color: '#9ca3af', fontSize: '11px' }}>+{b.skills.length - 2}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Users size={20} color="#9ca3af" />
            </div>
            <p style={{ color: '#374151', fontSize: '14px', fontWeight: '500', margin: '0 0 4px' }}>No bench resources yet</p>
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 16px' }}>Add your available candidates to start submitting</p>
            <button onClick={() => setActive('bench')} style={{ padding: '8px 16px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>
              Add First Resource
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
